import React, { lazy } from 'react';
import { Redirect, useLocation } from 'react-router-dom';

// Routes
import {
  RunSheet,
  Settings,
  Floorplan,
  Timeline,
  EditFloorplan,
  Integrations,
  Guestbook,
  MarketPlaceIntegrations,
  Calendar,
  VoucherRedemptions,
} from '@eatclubapp/ec-bookings-library';
import { BalanceHistory } from '../components/balanceHistory';

import CircleCheckIcon from '../assets/icons/circle_check.svg';
import CancelMembership from '../screens/cancelMembership/CancelMembership';
import { FEATURE_SCOPES } from '../utils/permissions';

/** Old path; keeps query string (e.g. ?activate=growDatabase) */
const AudiencesNetworksLegacyRedirect = () => {
  const { search } = useLocation();
  return <Redirect to={{ pathname: '/audience-targeting', search }} />;
};

const Logout = lazy(() => import('../components/auth/Logout'));
const Billing = lazy(() => import('../components/billing/Billing'));
const Bookings = lazy(() => import('../components/bookings/Bookings'));
const EditDeal = lazy(() => import('../components/deals/editDeal/EditDeal'));
const EditOffer = lazy(() => import('../components/deals/editOffer/EditOffer'));
const AudienceTargeting = lazy(() => import('../screens/audienceTargeting/AudienceTargeting'));
const PayBalance = lazy(() => import('../components/payBalance/PayBalance'));
// const BookingsLandingPageTitle = lazy(
//   () => import('../components/liveBookings/BookingsLanding/BookingsLandingPageTitle'),
// );

const Menus = lazy(() => import('../components/menus/Menus'));
const NotFound = lazy(() => import('../components/notFound/NotFound'));
const AllOffers = lazy(() => import('../components/offers/AllOffers/AllOffers'));
const CreateGoal = lazy(() => import('../components/offers/CreateGoal/CreateGoal'));
const EditGoal = lazy(() => import('../components/offers/EditGoal/EditGoal'));
const ViewGoal = lazy(() => import('../components/offers/ViewGoal/ViewGoal'));
const Offers = lazy(() => import('../components/offers/OffersDashboard/Offers'));
const PreviousGoalsScreen = lazy(() => import('../components/offers/PreviousGoalsScreen'));
const Support = lazy(() => import('../components/support/Support'));
const HelpCentre = lazy(() => import('../components/HelpCentre/HelpCentre'));
const Profile = lazy(() => import('../components/profile/Profile'));
const BillingDetails = lazy(() => import('../screens/profile/BillingDetails'));
const Reactivate = lazy(() => import('../screens/reactivate/Reactivate'));
const Settlements = lazy(() => import('../components/settlements/Settlements'));
const SettlementsContainer = lazy(() => import('../components/settlements/SettlementsContainer'));
const OfferPerformanceShell = lazy(() => import('../screens/analytics/OfferPerformanceShell'));
const BusinessReview = lazy(() => import('../screens/businessReview/BusinessReview'));
const ForecastPage = lazy(() => import('../components/insightsDashboard/ForecastPage'));
const AnalyticsPage = lazy(() => import('../components/insightsDashboard/AnalyticsPage'));
const CreateOffer = lazy(() => import('../components/offers/CreateOffer/CreateOffer'));
const EditRequestSuccess = lazy(() => import('../components/offers/EditRequestSuccess'));
const OnboardingTour = lazy(() => import('../screens/onboardingTour/OnboardingTour'));
const GuestManager = lazy(() => import('../screens/guestManager/GuestManager'));
const SegmentationTags = lazy(() => import('../screens/segmentationTags/SegmentationTags'));
const DietaryTags = lazy(() => import('../screens/segmentationTags/DietaryTags'));
const HolidayHours = lazy(() => import('../screens/holidayHours/HolidayHours'));
const BusinessAnalytics = lazy(
  () => import('../screens/landing/businessAnalytics/BusinessAnalytics'),
);
const LandingForecast = lazy(() => import('../screens/landing/forecast/LandingForecast'));
const LandingBooking = lazy(() => import('../screens/landing/booking/LandingBooking'));
const PosIntegrationRouter = lazy(() => import('../screens/posIntegration/PosIntegrationRouter'));

const routes = [
  {
    path: '/landing-analytics',
    pageTitle: 'Business Analytics',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: false,
    main: () => <BusinessAnalytics />,
    header: 'forecasting',
    disableDrawerMarginOnMdDown: true,
    showMobileHeader: true,
  },
  {
    path: '/landing-forecast',
    pageTitle: 'Forecast',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: false,
    main: () => <LandingForecast />,
    header: 'forecasting',
    disableDrawerMarginOnMdDown: true,
    showMobileHeader: true,
  },
  {
    // Legacy URL — redirects to canonical /livebookings entry point (SEO/redirect audit)
    path: '/landing-booking',
    exact: true,
    main: () => <Redirect to='/livebookings' />,
  },
  {
    path: '/insights/customers',
    pageTitle: 'Offer performance',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: true,
    main: () => <OfferPerformanceShell />,
    offerPerformanceTabs: true,
    allowedScopes: FEATURE_SCOPES.INSIGHTS,
  },
  {
    path: '/insights',
    pageTitle: 'Offer performance',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: true,
    main: () => <OfferPerformanceShell />,
    offerPerformanceTabs: true,
    allowedScopes: FEATURE_SCOPES.INSIGHTS,
  },
  {
    path: '/customer-value',
    pageTitle: 'Customers',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: false,
    main: () => <Redirect to='/insights/customers' />,
  },
  {
    path: '/forecast',
    pageTitle: 'Forecast',
    blockWhiteLabel: false,
    forecasterEnabledRequired: true,
    showDebtBanner: true,
    main: () => <ForecastPage />,
    allowedScopes: FEATURE_SCOPES.INSIGHTS,
    header: 'forecasting',
    disableDrawerMarginOnMdDown: true,
    showMobileHeader: true,
    showDisabledBanner: true, // TODO support on forecasting layout
  },
  {
    path: '/analytics',
    pageTitle: 'Analytics',
    blockWhiteLabel: false,
    analyticsEnabledRequired: true,
    showDebtBanner: true,
    main: () => <AnalyticsPage />,
    allowedScopes: FEATURE_SCOPES.INSIGHTS,
    header: 'forecasting',
    disableDrawerMarginOnMdDown: true,
    showMobileHeader: true,
    showDisabledBanner: true, // TODO support on forecasting layout
  },
  // {
  //   path: '/deals',
  //   pageTitle: 'Deals',
  //   exact: true,
  //   blockWhiteLabel: true,
  //   main: () => <Deals />,
  // },
  // {
  //   path: '/deals/create',
  //   pageTitle: 'Create Deal',
  //   exact: true,
  //   blockWhiteLabel: true,
  //   backNavigation: {
  //     label: 'Back',
  //     route: '/offers',
  //   },
  //   main: () => <CreateDeal />,
  // },
  {
    path: '/deals/edit/:dealId',
    pageTitle: 'Edit Deal',
    exact: false,
    blockWhiteLabel: false,
    main: () => <EditDeal />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/offers',
    pageTitle: 'Exclusive offers',
    exact: true,
    blockWhiteLabel: true,
    main: () => <Offers />,
  },
  {
    path: '/offers/active',
    pageTitle: 'All offers',
    exact: true,
    blockWhiteLabel: true,
    backNavigation: {
      label: 'Back',
      route: '/offers',
      goBack: true,
    },
    main: () => <AllOffers />,
  },
  {
    path: '/offers/create',
    pageTitle: 'Create new offers',
    exact: true,
    blockWhiteLabel: true,
    backNavigation: {
      label: 'Back',
      route: '/offers',
      goBack: true,
    },
    main: () => <CreateOffer />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/offers/edit/request/success',
    pageIcon: <CircleCheckIcon />,
    pageTitle: 'Request sent!',
    exact: false,
    blockWhiteLabel: false,
    main: () => <EditRequestSuccess />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/offers/edit/:dealId',
    pageTitle: 'Edit Offer',
    exact: false,
    blockWhiteLabel: false,
    backNavigation: {
      label: 'Back',
      route: '/offers',
      goBack: true, // If true, try to go back to the previous page in stack before the hardcoded route
    },
    main: () => <EditOffer />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/goals/previous',
    pageTitle: 'Previous Goals',
    exact: true,
    blockWhiteLabel: false,
    backNavigation: {
      label: 'Back to offers',
      route: '/offers',
    },
    main: () => <PreviousGoalsScreen />,
  },
  {
    path: '/goals/create',
    pageTitle: '',
    exact: true,
    blockWhiteLabel: false,
    main: () => <CreateGoal />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/goals/edit/:goalId',
    pageTitle: 'Edit Goal',
    exact: false,
    blockWhiteLabel: false,
    backNavigation: {
      label: 'Back',
      route: '/offers',
    },
    main: () => <EditGoal />,
    allowedScopes: FEATURE_SCOPES.OFFERS,
  },
  {
    path: '/goals/:goalId',
    pageTitle: '',
    exact: false,
    blockWhiteLabel: false,
    backNavigation: {
      label: 'Back',
      route: '/offers',
    },
    main: () => <ViewGoal />,
  },
  {
    path: '/transactions',
    pageTitle: 'Transactions',
    exact: false,
    blockWhiteLabel: false,
    main: () => <Bookings />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.TRANSACTIONS,
  },
  {
    path: '/settlements',
    pageTitle: 'Settlements',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: true,
    main: () => <SettlementsContainer />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    // todo: add in redirect from billing to settlements when ecpay is enabled for all regions
    path: '/billing',
    pageTitle: 'Billing & Invoices',
    exact: false,
    blockWhiteLabel: false,
    main: () => <Billing />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    path: '/settlements/historical-statements',
    pageTitle: 'Billing & Invoices',
    exact: true,
    blockWhiteLabel: false,
    blockPartners: true,
    main: () => <Billing />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    path: '/settlements/balance-history',
    pageTitle: 'Balance history',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: true,
    main: () => <BalanceHistory />,
    backNavigation: {
      label: 'Back',
      route: '/settlements',
    },
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    path: '/settlements/pay-balance',
    pageTitle: '',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: true,
    main: () => <PayBalance />,
    backNavigation: {
      label: 'Back',
      route: '/settlements/balance-history',
    },
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.SETTLEMENTS,
  },
  {
    path: '/support',
    pageTitle: 'Support',
    exact: false,
    blockWhiteLabel: false,
    main: () => <Support />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.HELP_CENTRE,
  },
  {
    path: '/help-centre',
    pageTitle: 'Help Centre',
    exact: false,
    blockWhiteLabel: false,
    main: () => <HelpCentre />,
    allowIfDisabled: true,
    allowedScopes: FEATURE_SCOPES.HELP_CENTRE,
  },
  {
    path: '/menus',
    pageTitle: 'Menus',
    exact: true,
    blockWhiteLabel: false,
    main: () => <Menus />,
    allowedScopes: FEATURE_SCOPES.MENUS,
  },
  {
    path: '/pos-integration',
    pageTitle: 'POS Integration',
    exact: true,
    blockWhiteLabel: false,
    hideHeader: true,
    includePadding: true,
    main: () => <PosIntegrationRouter />,
  },
  {
    path: '/audience-targeting',
    pageTitle: 'Audience targeting',
    exact: false,
    blockWhiteLabel: false,
    main: () => <AudienceTargeting />,
    allowedScopes: FEATURE_SCOPES.AUDIENCE_TARGETING,
  },
  {
    path: '/audiences-networks',
    pageTitle: 'Audience targeting',
    exact: false,
    blockWhiteLabel: false,
    main: () => <AudiencesNetworksLegacyRedirect />,
  },
  {
    path: '/livebookings',
    pageTitle: 'Bookings',
    exact: true,
    blockWhiteLabel: false,
    showDebtBanner: false,
    main: () => <LandingBooking />,
    header: 'forecasting',
    disableDrawerMarginOnMdDown: true,
    showMobileHeader: true,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
    allowIfDisabled: true,
  },
  {
    path: '/livebookings/runsheet',
    pageTitle: 'Run sheet',
    id: 'runsheet',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    main: () => <RunSheet />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/monthly',
    pageTitle: 'Monthly',
    id: 'monthly',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    main: () => <Calendar />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/floorplan',
    pageTitle: 'Floor Plan',
    id: 'floorplan',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    obeePremiumRequired: true,
    main: () => <Floorplan />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/floorplan/edit',
    pageTitle: 'Edit Floor Plan',
    id: 'editFloorplan',
    exact: true,
    blockWhiteLabel: false,
    header: 'custom',
    hideNavigation: true,
    forceHideNavigation: true, // No option to show on this screen
    obeeEnabledRequired: true,
    main: () => <EditFloorplan />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/timeline',
    pageTitle: 'Timeline',
    id: 'timeline',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    obeePremiumRequired: true,
    main: () => <Timeline />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/guestbook',
    pageTitle: 'Guest book',
    id: 'guestbook',
    exact: true,
    blockWhiteLabel: false,
    header: 'obee',
    obeeEnabledRequired: true,
    obeePremiumRequired: true,
    main: () => <Guestbook />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  // {
  //   path: '/livebookings/calendar',
  //   pageTitle: 'Calendar',
  //   exact: true,
  //   blockWhiteLabel: false,
  //   header: 'obee',
  //   main: () => <Calendar />,
  // },
  {
    path: '/livebookings/voucher-redemptions',
    pageTitle: 'Voucher Redemptions',
    id: 'voucherRedemptions',
    exact: true,
    header: 'obee',
    hideFilters: true,
    main: () => <VoucherRedemptions />,
    obeeEnabledRequired: true,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/settings',
    pageTitle: 'Settings',
    exact: true,
    id: 'settings',
    blockWhiteLabel: false,
    header: 'obee',
    // hideNavigation: true,
    obeeEnabledRequired: true,
    // forceHideNavigation: true,
    main: () => <Settings />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/livebookings/integrations/fbmarketplace',
    pageTitle: 'Bookings integrations',
    exact: true,
    blockWhiteLabel: false,
    header: 'custom',
    hideNavigation: true,
    obeeEnabledRequired: true,
    forceHideNavigation: true,
    main: () => <MarketPlaceIntegrations />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
    allowIfDisabled: true,
  },
  {
    path: '/livebookings/integrations',
    pageTitle: 'Bookings integrations',
    exact: true,
    blockWhiteLabel: false,
    header: 'custom',
    hideNavigation: true,
    obeeEnabledRequired: true,
    forceHideNavigation: true,
    main: () => <Integrations />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/logout',
    pageTitle: 'Logout',
    blockWhiteLabel: false,
    header: 'custom',
    main: () => <Logout />,
  },
  {
    path: '/profile',
    pageTitle: 'Profile',
    exact: true,
    blockWhiteLabel: false,
    main: () => <Profile />,
    allowedScopes: FEATURE_SCOPES.PROFILE,
  },
  {
    path: '/profile/billing',
    pageTitle: 'Billing details',
    exact: true,
    blockWhiteLabel: false,
    main: () => <BillingDetails />,
    allowedScopes: FEATURE_SCOPES.PROFILE, // Only admins/managers should be able to access this
  },
  {
    path: '/reactivate',
    pageTitle: 'Reactivate account',
    exact: true,
    blockWhiteLabel: false,
    main: () => <Reactivate />,
    allowedScopes: FEATURE_SCOPES.PROFILE, // Only admins/managers should be able to access this
  },
  {
    path: '/tour',
    pageTitle: 'Onboarding tour',
    blockWhiteLabel: false,
    main: () => <OnboardingTour />,
    hideHeader: true,
    hideMobileHeader: true,
    hideNavigation: true,
    forceHideNavigation: true,
    allowHideNavigation: true,
    allowIfDisabled: true,
    showDisabledBanner: false, // Prevent showing the disabled banner on this screen
  },
  {
    path: '/business-review',
    pageTitle: 'Quarterly business review',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: false,
    hideNavigation: true,
    // hideHeader: true,
    // hideMobileHeader: true,
    // forceHideNavigation: false,
    // allowHideNavigation: true,
    header: 'custom',
    main: () => <BusinessReview />,
  },
  {
    path: '/profile/holiday-hours',
    pageTitle: 'Holiday hours',
    exact: true,
    blockWhiteLabel: false,
    ecPayEnabledRequired: false,
    showDebtBanner: false,
    main: () => <HolidayHours />,
  },
  {
    path: '/cancel',
    pageTitle: 'Cancel membership',
    blockWhiteLabel: false,
    main: () => <CancelMembership />,
    hideHeader: true,
  },
  {
    path: '/guest-manager',
    pageTitle: 'Guest manager',
    exact: true,
    blockWhiteLabel: true,
    main: () => <GuestManager />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/segmentation-tags',
    pageTitle: 'Segmentation tags',
    exact: true,
    blockWhiteLabel: true,
    obeeEnabledRequired: true,
    main: () => <SegmentationTags />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '/segmentation-tags/dietary',
    pageTitle: 'Dietary tags',
    exact: true,
    blockWhiteLabel: true,
    backNavigation: {
      label: 'Back',
      route: '/segmentation-tags',
    },
    obeeEnabledRequired: true,
    main: () => <DietaryTags />,
    allowedScopes: FEATURE_SCOPES.BOOKINGS,
  },
  {
    path: '*',
    pageTitle: '',
    exact: false,
    blockWhiteLabel: false,
    main: () => <NotFound />,
  },
];

export default routes;
